import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { withNamespaces } from "react-i18next";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Button, Card, CardBody, Col, Row, Spinner, Table, UncontrolledTooltip } from "reactstrap";
import { EMPTY_LIST } from "src/common/constants";
import OverviewHeader from "../../../../../../components/overview-header";
import { useHistory, useLocation } from "react-router-dom";
import ComplianceScreeningService from 'src/modules/3rd-party-management/apis/admin/compliance/screening/ScreeningService';
import { INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';
import DateUtils from "src/services/utils/DateUtils";
import { RiAddLine, RiSubtractLine } from "react-icons/ri";
import { KYBScreeningDataSets, KYBScreeningTypes, ROUTE_PATH_ADMIN_NEW_COMPLIANCE_QUERY } from "src/modules/3rd-party-management/constants";
import FilteringComponent from "./components/FilteringComponent";

const searchInputsColsCount = 9;

const colors = {
    searchInputCol: {
        bg: '#FFDADA'
    },
    searchResultCol: {
        bg: '#E9E9EA'
    }
}

const SearchResults = ({ searchedPersonsResults, t, colWidths, screeningType }) => {
    const dateUtils = new DateUtils();
    const history = useHistory();

    const leftOffset = colWidths
        .slice(0, searchInputsColsCount)
        .reduce((a, b) => a + b, 0);

    return (
        <div
            className="expanded-wrapper"
            style={{
                marginLeft: leftOffset,
                backgroundColor: `${colors.searchResultCol.bg}`
            }}
        >
            <Table className="mb-0">
                <tbody>
                    {searchedPersonsResults.slice(1).map((eachResult, resultIndex) => {
                        let searchResultsStartColIndex = searchInputsColsCount;

                        return (
                            <tr
                                key={resultIndex + 1}
                            >
                                <td
                                    style={{
                                        width: `${colWidths[searchResultsStartColIndex++]}px`,
                                        backgroundColor: `${colors.searchResultCol.bg}`
                                    }}
                                    className="color-gray"
                                >{eachResult.id}</td>
                                <td
                                    style={{
                                        width: `${colWidths[searchResultsStartColIndex++]}px`,
                                        backgroundColor: `${colors.searchResultCol.bg}`
                                    }}
                                >
                                    {eachResult.evidencesCount ?? null}
                                    {/* {eachResult.findings?.length > 0 && (
                                        <Button
                                            color="primary"
                                            outline
                                            className="border-0"
                                        >
                                            {t('View')}
                                        </Button>
                                    )} */}
                                </td>
                                <td
                                    style={{
                                        width: `${colWidths[searchResultsStartColIndex++]}px`,
                                        backgroundColor: `${colors.searchResultCol.bg}`
                                    }}
                                >
                                    {(eachResult.datasets || []).map((datasetKey, index) => {
                                        return (
                                            <span
                                                key={index}
                                                id={`row-${resultIndex + 1}-dataset-${index}`}
                                            >
                                                {t(KYBScreeningDataSets[datasetKey]?.title)}{index < eachResult.datasets.length - 1 ? ', ' : ''}

                                                <UncontrolledTooltip
                                                    target={`row-${resultIndex + 1}-dataset-${index}`}
                                                >
                                                    {t(KYBScreeningDataSets[datasetKey]?.description)}
                                                </UncontrolledTooltip>
                                            </span>
                                        )
                                    })}
                                </td>
                                <td
                                    style={{
                                        width: `${colWidths[searchResultsStartColIndex++]}px`,
                                        backgroundColor: `${colors.searchResultCol.bg}`
                                    }}
                                >
                                    {eachResult.falsePositive ? t('Yes') : t('No')}
                                </td>
                                <td
                                    style={{
                                        width: `${colWidths[searchResultsStartColIndex++]}px`,
                                        backgroundColor: `${colors.searchResultCol.bg}`
                                    }}
                                >
                                    {(eachResult.datesOfBirth || []).map((brithDate) => {
                                        return dateUtils.convertDateToDate(
                                            brithDate,
                                            INTERNATIONAL_DATE_FORMAT
                                        );
                                    }).join(', ')}
                                </td>
                                <td
                                    style={{
                                        width: `${colWidths[searchResultsStartColIndex++]}px`,
                                        backgroundColor: `${colors.searchResultCol.bg}`
                                    }}
                                >
                                    <div className="actions">
                                        <Button
                                            color="primary"
                                            aria-label={t('View result Detail')}
                                            outline
                                            className="me-1 border-0"
                                            onClick={() => {
                                                history.push({
                                                    search: `?tab=compliance-screening&id=${eachResult.id}&type=${screeningType}`
                                                });
                                            }}
                                        >
                                            <i className="ri-eye-line font-size-20" />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </Table>
        </div>
    )        
};

const CustomExpandColumn = ({ expandable, expanded }) => {
    if (!expandable) return null;

    return expanded ? <RiSubtractLine size="16" /> : <RiAddLine size="16" />;
}

const ScreeningTableHeaderLabels = ({ t, colWidths }) => {
    const searchInputContainerWidth = colWidths
        .slice(0, searchInputsColsCount)
        .reduce((a, b) => a + b, 0);

    const searchResultContainerWidth = colWidths
        .slice(searchInputsColsCount)
        .reduce((a, b) => a + b, 0);

    const totalContainerWidth = (searchInputContainerWidth + searchResultContainerWidth) - 4;
        
    return (
        <div 
            className="monitoring-table-header d-flex"
            style={{
                width: `${totalContainerWidth}px`
            }}    
        >
            <div 
                className="search-inputs-label d-flex align-items-center justify-content-center"
                style={{
                    width: `${searchInputContainerWidth}px`
                }}
            >
                <h6 className="mb-0">{t('Search Input')}</h6>
            </div>

            <div 
                className="search-results-label d-flex align-items-center justify-content-center"
                style={{
                    width: `${searchResultContainerWidth}px`
                }}
            >
                <h6 className="mb-0">{t('Search Result')}</h6>
            </div>
        </div>
    )
}

const ComplianceScreening = ({
    t,
    supplierId
}) => {
    const dateUtils = new DateUtils();

    const history = useHistory();
    const location = useLocation();
    const [filters, setFilters] = useState({
        pageIndex: 1,
        pageSize: 25
    });

    const wrapperRef = useRef(null);
    const [colWidths, setColWidths] = useState([]);
    const [nonExpandableRows, setNonExtendableRows] = useState([]);

    const tableColumns = [
        {
            dataField: "id",
            text: t("ID"),
            sort: false,
            key: 1,
            style: {
                backgroundColor: '#FFDADA'
            },
            headerClasses: "color-pink"
        },
        {
            dataField: "searchedPersonsQuery.name",
            text: t("Search Query (e.g., name)"),
            sort: false,
            key: 3,
            headerClasses: "color-pink",
            style: {
                backgroundColor: '#FFDADA'
            }
        },
        {
            dataField: "searchedPersonsQuery.rating",
            text: t("Risk Rating"),
            sort: false,
            key: 4,
            style: {
                backgroundColor: '#FFDADA'
            },
            headerClasses: "color-pink label",
            formatter: (cell) => {
                let color = 'bg-success';

                if (cell === 'medium') {
                    color = 'bg-warning';
                }
                else if (cell === 'high') {
                    color = 'bg-danger';
                }

                return (
                    <span
                        class={`dt-risk-rating-bullet ${color}`}
                    />
                )
            }
        },
        {
            dataField: "screeningType",
            text: t("Type"),
            sort: false,
            key: 5,
            style: {
                backgroundColor: '#FFDADA'
            },
            headerClasses: "color-pink",
            formatter: (cell) => {
                return t(KYBScreeningTypes[cell]?.title || cell);
            }
        },
        {
            dataField: "searchedPersonsQuery.dateOfBirth",
            text: t("Date of Birth"),
            sort: false,
            key: 6,
            headerClasses: "color-pink",
            style: {
                backgroundColor: '#FFDADA'
            },
            formatter: (cell) => {
                return cell ? dateUtils.convertDateToDate(
                    cell, 
                    INTERNATIONAL_DATE_FORMAT
                ) : '--';
            }
        },
        {
            dataField: "createdBy",
            text: t("Created by"),
            sort: false,
            key: 7,
            headerClasses: "color-pink",
            style: {
                backgroundColor: '#FFDADA'
            },
            formatter: (cell) => {
                return cell?.fullName || ''
            }
        },
        {
            dataField: "createdAt",
            text: t("Created on"),
            sort: false,
            key: 8,
            headerClasses: "color-pink",
            style: {
                backgroundColor: '#FFDADA'
            },
            formatter: (cell) => {
                return dateUtils.convertDateToDate(cell, INTERNATIONAL_DATE_FORMAT);
            }
        },
        {
            dataField: "searchedPersonsQuery.threshold",
            text: t("Fuzziness"),
            sort: false,
            key: 9,
            headerClasses: "color-pink",
            style: {
                backgroundColor: '#FFDADA'
            }
        },


        {
            dataField: "searchedPersonsResults",
            text: t("Finding ID"),
            sort: false,
            key: 10,
            headerClasses: "color-gray",
            style: {
                backgroundColor: '#E9E9EA'
            },
            formatter: (searchedPersonsResults) => {
                return searchedPersonsResults[0]?.id || '--'
            }
        },
        // {
        //     dataField: "searchedPersonsResults",
        //     text: t("Finding Source"),
        //     sort: false,
        //     key: 11,
        //     headerClasses: "color-gray",
        //     style: {
        //         backgroundColor: '#E9E9EA'
        //     },
        //     formatter: (searchedPersonsResults) => {
        //         return '--'
        //     }
        // },
        {
            dataField: "searchedPersonsResults",
            text: t("Findings"),
            sort: false,
            key: 12,
            headerClasses: "color-gray label",
            style: {
                backgroundColor: '#E9E9EA'
            },
            formatter: (cell) => {
                return cell[0]?.evidencesCount ?? null
                // if (searchedPersonsResults[0]?.evidencesCount) {
                //     return (
                //         <Button
                //             color="primary"
                //             outline
                //             className="border-0"
                //             onClick={() => {
                //                 history.push({
                //                     search: `?tab=findings&id=${searchedPersonsResults[0].findings[0]?.id}`
                //                 })
                //             }}
                //         >
                //             {t('View')}
                //         </Button>
                //     )
                // }

                // return null;
            }
        },
        {
            dataField: "searchedPersonsResults",
            text: t("Tags"),
            sort: false,
            key: 13,
            headerClasses: "color-gray",
            style: {
                backgroundColor: '#E9E9EA',
                width: '160px'
            },
            formatter: (searchedPersonsResults, row, rowIndex) => {
                const itemDatasets = searchedPersonsResults[0]?.datasets || [];

                return itemDatasets.map((datasetKey, index) => {
                    return (
                        <span
                            key={index}
                            id={`row-${rowIndex}-dataset-${index}`}
                        >
                            {t(KYBScreeningDataSets[datasetKey]?.title)}{index < itemDatasets.length - 1 ? ', ': ''}

                            <UncontrolledTooltip
                                target={`row-${rowIndex}-dataset-${index}`}
                            >
                                {t(KYBScreeningDataSets[datasetKey]?.description)}
                            </UncontrolledTooltip>
                        </span>
                    )
                })
            }
        },
        {
            dataField: "searchedPersonsResults",
            text: t("False Positive"),
            sort: false,
            key: 14,
            headerClasses: "color-gray",
            style: {
                backgroundColor: '#E9E9EA'
            },
            formatter: (searchedPersonsResults) => {
                return searchedPersonsResults.length > 0 ? 
                    (searchedPersonsResults[0]?.isFalsePositive ? t('Yes') : t('No')) : 
                    '---';
            }
        },
        {
            dataField: "searchedPersonsResults",
            text: t("Dates of Birth"),
            sort: false,
            key: 15,
            headerClasses: "color-gray",
            style: {
                backgroundColor: '#E9E9EA'
            },
            formatter: (searchedPersonsResults) => {
                return (searchedPersonsResults[0]?.datesOfBirth || []).map((brithDate) => {
                    return dateUtils.convertDateToDate(
                        brithDate, 
                        INTERNATIONAL_DATE_FORMAT
                    );
                }).join(', ')
            }
        },
        {
            dataField: "action",
            text: t("Action"),
            sort: false,
            key: 16,
            headerClasses: "color-gray",
            style: {
                backgroundColor: '#E9E9EA'
            },
            formatter: (_cell, { searchedPersonsResults, screeningType }) => {
                return (
                    <div className="actions">
                        {searchedPersonsResults.length > 0 && (
                            <Button
                                color="primary"
                                aria-label={t('View result Detail')}
                                outline
                                className="me-1 border-0"
                                onClick={() => {
                                    history.push({
                                        search: `?tab=compliance-screening&id=${searchedPersonsResults[0].id}&type=${screeningType}`
                                    });
                                }}
                            >
                                <i className="ri-eye-line font-size-20" />
                            </Button>
                        )}
                    </div>
                )
            }
        }
    ];

    const {
        data: screeningResult,
        isFetching: screeningResultIsFetching,
        isFetched: screeningResultFetched
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-compliance-screening-results',
            supplierId,
            filters
        ],
        queryFn: async () => {
            const service = ComplianceScreeningService.getInstance();

            return await service.fetchComplianceScreeningResult(supplierId, filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching the related incidents list.'), {
                type: 'error',
            });
        },
    });

    const NoDataIndication = () => (
        (screeningResultFetched && !(screeningResult?.result || []).length) ? <div className="alert m-0" role="alert">
            <p style={{
                textAlign: "center",
                marginBottom: 0
            }}>
                {t(EMPTY_LIST)}
            </p>
        </div> : <></>
    );

    const handleTableChange = (type, options) => {
        const {
            page,
            sizePerPage,

            sortField,
            sortOrder
        } = options;

        switch (type) {
            case 'pagination':
                setFilters({
                    ...filters,
                    pageIndex: page,
                    pageSize: sizePerPage
                });
                break;
            case 'sort':
                setFilters({
                    ...filters,
                    sortField: sortField,
                    sortOrder: sortOrder.toUpperCase()
                })
                break;
            default:
                return false;
        }
    }

    const measureFirstRow = useCallback(() => {
        if (!wrapperRef.current) return;

        const table = wrapperRef.current.querySelector("table");

        if (!table) return;

        const firstRow = table.querySelector(
            "thead tr"
        );

        if (!firstRow) return;

        const cells = firstRow.querySelectorAll("th");

        const widths = [...cells].map(th =>
            Math.round(th.getBoundingClientRect().width)
        );

        setColWidths(widths);
    }, []);

    useLayoutEffect(() => {
        measureFirstRow();
    }, [
        measureFirstRow
    ]);

    useEffect(() => {
        if (!wrapperRef.current) return;

        const observer = new ResizeObserver(() => {
            measureFirstRow();
        });

        observer.observe(wrapperRef.current);

        return () => observer.disconnect();
    }, [
        measureFirstRow
    ]);

    useEffect(() => {
        if (screeningResult?.result?.length) {
            setNonExtendableRows(
                screeningResult.result.filter(
                    (row) => row.searchedPersonsResults?.length <= 1 
                ).map((row) => row.id)
            )
        }
    }, [
        screeningResult?.result,
    ]);

    const expandRow = {
        renderer: row => <SearchResults {...row} t={t} colWidths={colWidths} />,
        expandColumnRenderer: (data) => <CustomExpandColumn {...data} />,
        expandHeaderColumnRenderer: () => null,
        showExpandColumn: true,
        expandByColumnOnly: true,
        onlyOneExpanding: true,
        expandColumnPosition: 'left',
        nonExpandable: nonExpandableRows
    };

    const totalItemsCount = parseInt(screeningResult?.itemsCount || 0);

    const handleFilterChange = useCallback((newFilters) => {
        setFilters((prev) => {
            return {
                ...prev,
                ...newFilters,
                pageIndex: 1
            }
        });
    }, []);

    return (
        <div className="compliance-monitoring">
            <Row>
                <Col sm="12" className="mb-5">
                    <OverviewHeader
                        supplierId={supplierId}
                    />
                </Col>

                <Col sm="12">
                    <div
                        style={{
                            position: 'sticky',
                            top: '74px',
                            zIndex: 10
                        }}
                        className="d-flex justify-content-end align-items-center mb-3"
                    >
                        <div className="d-flex justify-content-end align-items-center bg-white">
                            <div className="d-flex">
                                <FilteringComponent
                                    onChange={handleFilterChange}
                                />
                            </div>

                            <div>
                                <Button
                                    color="primary"
                                    type="button"
                                    onClick={() => {
                                        history.push(`${ROUTE_PATH_ADMIN_NEW_COMPLIANCE_QUERY}?${new URLSearchParams({
                                            thirdparties: supplierId,
                                            type: 'screening'
                                        }).toString()}`, {
                                            from: `${location.pathname}${location.search}`
                                        });
                                    }}
                                >
                                    {t('Start {{complianceType}}', {
                                        complianceType: 'Compliance Screening'
                                    })}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Col>

                <Col
                    sm="12"
                    md="12"
                    className="mb-4"
                >
                    <Card className="shadow-none">
                        <CardBody className="p-0">
                            <PaginationProvider
                                pagination={
                                    paginationFactory({
                                        custom: true,
                                        page: filters.pageIndex,
                                        sizePerPage: filters.pageSize,
                                        totalSize: totalItemsCount,
                                        withFirstAndLast: true,
                                        alwaysShowAllBtns: true,
                                        prePageText: <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                                        nextPageText: <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                                        prePageTitle: t('Pre page'),
                                        firstPageTitle: t('First page'),
                                        lastPageTitle: t('Last page'),
                                        showTotal: false,
                                        paginationTotalRenderer: (from, to, size) => {
                                            return (
                                                <span className="react-bootstrap-table-pagination-total">
                                                    {`${t('Total Items')} ${size}`}
                                                </span>
                                            )
                                        },
                                        hideSizePerPage: false,
                                        sizePerPageList: [
                                            {
                                                text: '25',
                                                value: 25
                                            },
                                            {
                                                text: '50',
                                                value: 50
                                            }
                                        ]
                                    })
                                }>
                                {
                                    ({
                                        paginationProps,
                                        paginationTableProps
                                    }) => (
                                        <>
                                            <Row>
                                                <Col sm="12" className="mt-4">
                                                    <div ref={wrapperRef}>
                                                        <div className="table-responsive">
                                                            <ScreeningTableHeaderLabels
                                                                t={t}
                                                                colWidths={colWidths}
                                                            />

                                                            <BootstrapTable
                                                                remote={{
                                                                    pagination: true,
                                                                    filter: false,
                                                                    sort: true,
                                                                    cellEdit: false,
                                                                    search: false
                                                                }}
                                                                loading={screeningResultIsFetching}
                                                                overlay={overlayFactory({ spinner: <Spinner animation="border" variant="primary" size="md" />, text: `${t("Loading")}...` })}
                                                                onTableChange={handleTableChange}
                                                                defaultSorted={[]}
                                                                keyField="id"
                                                                responsive={false}
                                                                bordered={false}
                                                                data={screeningResult?.result || []}
                                                                striped={false}
                                                                columns={tableColumns}
                                                                // wrapperClasses="table-responsive"
                                                                classes={"table tpdd-table compliance-monitoring-table"}
                                                                headerWrapperClasses={"thead-light"}
                                                                style={{
                                                                    overflowX: "auto",
                                                                }}
                                                                noDataIndication={() => <NoDataIndication />}
                                                                expandRow={expandRow}
                                                                {...paginationTableProps}
                                                            />
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col sm="12">
                                                    <div className="tpdd-pagination-style-1">
                                                        <PaginationTotalStandalone
                                                            {...paginationProps}
                                                        />

                                                        {totalItemsCount > filters.pageSize && (
                                                            <div>
                                                                <PaginationListStandalone
                                                                    {...paginationProps}
                                                                />

                                                                <SizePerPageDropdownStandalone
                                                                    {...paginationProps}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </Col>
                                            </Row>
                                        </>
                                    )
                                }

                            </PaginationProvider>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    )
};

export default withNamespaces()(
    ComplianceScreening
);